'use client';

import { useEffect, useId, useRef, useState, useSyncExternalStore, ChangeEvent, FormEvent } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import api from '@/lib/axios';
import useAuthStore from '@/stores/authStore';
import styles from './landing.module.css';
import { landingFontVars } from './fonts';
import {
    ADDONS,
    CONTACT,
    LANDING_MODE,
    MIN_BERLANGGANAN_BULAN,
    MODUL_OPTIONS,
    PAKET,
    TESTIMONI,
    TRIAL_DAYS,
    WA_CHAT_MESSAGE,
    Paket,
    PublicPlan,
    buildDemoRequestMessage,
    buildPaketMessage,
    buildSignupUrl,
    buildWaUrl,
    formatRupiah,
    mergePublicPlans,
    formatWaNumber,
    normalizeWaInput,
} from './landingConfig';

// Pratinjau aplikasi hanya dirender di klien: HTML awal tidak memuat isi kanvas kosong ("Tidak ada data",
// nama menu sidebar, h1 kedua), dan DataTable/sweetalert2/Sidebar terpecah ke chunk terpisah.
// Placeholder memakai kelas bingkai yang sama supaya ukuran hero tidak bergeser.
const AppPreview = dynamic(() => import('./AppPreview'), {
    ssr: false,
    loading: () => <div className={styles.shot} aria-hidden="true" />,
});

// ─── Data konten ──────────────────────────────────────────────────────────────

interface ModuleItem {
    kode: string;
    nama: string;
    ket: string;
}

const MODULES: ModuleItem[] = [
    { kode: 'GL', nama: 'General Ledger', ket: 'Perkiraan, jurnal, posting, neraca, dan laba rugi.' },
    { kode: 'KB', nama: 'Kas & Bank', ket: 'Kas, rekening bank, giro masuk dan keluar, pencairan, tolakan, dan penggantian giro.' },
    { kode: 'AP', nama: 'Hutang', ket: 'Saldo awal, pembayaran, uang muka, serta koreksi CN dan DN ke supplier.' },
    { kode: 'AR', nama: 'Piutang', ket: 'Saldo awal, pembayaran, koreksi, uang muka, dan pencairan kartu.' },
    { kode: 'IN', nama: 'Inventory', ket: 'Gudang, satuan, barang, saldo awal, transfer, LPB, dan stok opname.' },
    { kode: 'PB', nama: 'Pembelian', ket: 'Supplier, PO, pembelian, retur, dan biaya pembelian.' },
    { kode: 'PJ', nama: 'Penjualan', ket: 'Customer, sales, harga jual, SO, penjualan, dan retur.' },
    { kode: 'SY', nama: 'Tools', ket: 'User, level akses, info perusahaan, dan hitung ulang saldo.' },
];

interface FlowStep {
    no: string;
    nama: string;
    ket: string;
}

const FLOW_STEPS: FlowStep[] = [
    { no: '01', nama: 'PO', ket: 'Pesan ban dan sparepart ke supplier' },
    { no: '02', nama: 'LPB', ket: 'Gudang terima barang, stok bertambah' },
    { no: '03', nama: 'Hutang', ket: 'Faktur supplier tercatat otomatis' },
    { no: '04', nama: 'Kas Bank', ket: 'Bayar lewat transfer atau giro' },
    { no: '05', nama: 'Jurnal', ket: 'Terbentuk otomatis, tinggal posting' },
    { no: '06', nama: 'Neraca', ket: 'Laporan siap dibaca manajer' },
];

interface MigPoint {
    judul: string;
    ket: string;
}

const MIG_POINTS: MigPoint[] = [
    // `*` → catatan di bawah daftar (versi < 3 & custom perlu penyesuaian) — pak Kristian 2026-09-29
    { judul: 'Struktur data sama.', ket: 'Skema database mengikuti MGMX desktop, jadi data lama bisa dibawa.*' },
    { judul: 'Banyak pengguna sekaligus.', ket: 'Admin, kasir, gudang, dan manajer login bersamaan dari perangkat masing-masing.' },
    { judul: 'Tanpa instal di tiap PC.', ket: 'Pembaruan cukup dipasang sekali di server.' },
];

interface DemoGet {
    judul: string;
    ket: string;
}

const DEMO_GETS: DemoGet[] = [
    { judul: 'Kenali kebutuhan', ket: 'Kami tanyakan alur kerja kantor dan armada Anda.' },
    { judul: 'Demo modul yang relevan', ket: 'Hanya modul yang Anda pakai, bukan tur semua menu.' },
    { judul: 'Rencana migrasi', ket: 'Perkiraan waktu dan langkah pindah dari sistem lama.' },
];

const WA_CHAT_URL = buildWaUrl(WA_CHAT_MESSAGE);

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// ─── Helper ───────────────────────────────────────────────────────────────────

function Logo({ className }: { className?: string }) {
    return (
        <div className={clsx(styles.logo, className)}>
            <i className={styles.logoMark} aria-hidden="true" />MGMX
        </div>
    );
}

// Status login untuk gate landing. Server snapshot selalu `false` → saat hydration
// render sama dengan SSR (tanpa mismatch); saat navigasi klien (mis. router.push('/')
// dari AccessDenied) token langsung terbaca sehingga landing tidak pernah ter-render.
// Token JWT kedaluwarsa (klaim exp) = belum login, sama dengan script inline di app/page.tsx.
const subscribeAuth = (onChange: () => void) => useAuthStore.subscribe(onChange);

/** true bila klaim `exp` JWT sudah lewat. Token tanpa exp / tidak terbaca → dianggap masih berlaku. */
function isTokenExpired(token: string): boolean {
    try {
        const part = token.split('.')[1];
        if (!part) return false;
        let b64 = part.replace(/-/g, '+').replace(/_/g, '/');
        while (b64.length % 4) b64 += '=';
        const payload: unknown = JSON.parse(window.atob(b64));
        const exp = payload && typeof payload === 'object' ? (payload as { exp?: unknown }).exp : undefined;
        return typeof exp === 'number' && exp * 1000 <= Date.now();
    } catch {
        return false;
    }
}

const getHasToken = (): boolean => {
    const token = useAuthStore.getState().accessToken;
    return !!token && !isTokenExpired(token);
};
const getServerHasToken = (): boolean => false;

// ─── Form Minta Demo ──────────────────────────────────────────────────────────

type FieldKey = 'nama' | 'pt' | 'wa' | 'email';

type DemoFormErrors = Partial<Record<FieldKey, string>>;

const FIELD_ORDER: FieldKey[] = ['nama', 'pt', 'wa', 'email'];

interface DemoSent {
    url: string;
    nama: string;
    wa: string;
    /** false bila window.open diblokir browser (popup blocker). */
    opened: boolean;
}

function DemoRequest() {
    const uid = useId();
    const ids = {
        nama: `${uid}-nama`,
        pt: `${uid}-pt`,
        wa: `${uid}-wa`,
        email: `${uid}-email`,
        modul: `${uid}-modul`,
        note: `${uid}-note`,
    };
    const errId = (k: FieldKey) => `${uid}-${k}-err`;

    const [nama, setNama] = useState('');
    const [pt, setPt] = useState('');
    const [wa, setWa] = useState('');
    const [email, setEmail] = useState('');
    const [modul, setModul] = useState<string[]>([]);
    const [errors, setErrors] = useState<DemoFormErrors>({});
    const [sent, setSent] = useState<DemoSent | null>(null);

    const namaRef = useRef<HTMLInputElement>(null);
    const ptRef = useRef<HTMLInputElement>(null);
    const waRef = useRef<HTMLInputElement>(null);
    const emailRef = useRef<HTMLInputElement>(null);
    const doneRef = useRef<HTMLDivElement>(null);
    const focusAfterReset = useRef(false);

    // Fokus ke pesan sukses setelah kirim; kembali ke kolom pertama setelah "isi ulang".
    useEffect(() => {
        if (sent) {
            doneRef.current?.focus();
        } else if (focusAfterReset.current) {
            focusAfterReset.current = false;
            namaRef.current?.focus();
        }
    }, [sent]);

    const clearError = (key: FieldKey) => {
        if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
    };

    const handleNamaChange = (e: ChangeEvent<HTMLInputElement>) => {
        setNama(e.target.value);
        clearError('nama');
    };

    const handlePtChange = (e: ChangeEvent<HTMLInputElement>) => {
        setPt(e.target.value);
        clearError('pt');
    };

    const handleWaChange = (e: ChangeEvent<HTMLInputElement>) => {
        setWa(e.target.value.replace(/[^0-9+\-\s()]/g, ''));
        clearError('wa');
    };

    const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        clearError('email');
    };

    const toggleModul = (opt: string) => {
        setModul((prev) => (prev.includes(opt) ? prev.filter((m) => m !== opt) : [...prev, opt]));
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const namaClean = nama.trim().replace(/\s+/g, ' ');
        const ptClean = pt.trim().replace(/\s+/g, ' ');
        const emailClean = email.trim();
        const waNormalized = normalizeWaInput(wa);

        const next: DemoFormErrors = {};
        if (!namaClean) next.nama = 'Nama lengkap wajib diisi.';
        if (!ptClean) next.pt = 'Nama perusahaan wajib diisi.';
        if (!wa.trim()) {
            next.wa = 'Nomor WhatsApp wajib diisi.';
        } else if (!waNormalized) {
            next.wa = 'Nomor WhatsApp tidak valid. Contoh: 812 3456 7890.';
        }
        if (emailClean && !EMAIL_PATTERN.test(emailClean)) {
            next.email = 'Format email tidak valid. Contoh: budi@perusahaan.co.id.';
        }
        setErrors(next);

        const firstInvalid = FIELD_ORDER.find((k) => next[k]);
        if (firstInvalid || !waNormalized) {
            const refs = { nama: namaRef, pt: ptRef, wa: waRef, email: emailRef };
            refs[firstInvalid ?? 'wa'].current?.focus();
            return;
        }

        const url = buildWaUrl(buildDemoRequestMessage({
            nama: namaClean,
            perusahaan: ptClean,
            waNormalized,
            email: emailClean || undefined,
            // urutan pesan mengikuti urutan chip, bukan urutan klik
            modul: MODUL_OPTIONS.filter((m) => modul.includes(m)),
        }));
        const win = window.open(url, '_blank');
        if (win) win.opener = null;
        setSent({ url, nama: namaClean, wa: formatWaNumber(waNormalized), opened: !!win });
    };

    const handleReset = () => {
        setNama('');
        setPt('');
        setWa('');
        setEmail('');
        setModul([]);
        setErrors({});
        focusAfterReset.current = true;
        setSent(null);
    };

    /** Atribut a11y + pesan error inline untuk satu kolom teks. */
    const fieldA11y = (k: FieldKey) => ({
        'aria-invalid': errors[k] ? true : undefined,
        'aria-describedby': errors[k] ? errId(k) : undefined,
    });

    const renderError = (k: FieldKey) => (
        <span aria-live="polite">
            {errors[k] && <span id={errId(k)} className={styles.fieldErr}>{errors[k]}</span>}
        </span>
    );

    if (sent) {
        return (
            <div ref={doneRef} className={styles.done} tabIndex={-1}>
                <span className={clsx(styles.doneIcon, !sent.opened && styles.doneIconWait)} aria-hidden="true">
                    {sent.opened ? '✓' : '!'}
                </span>
                <h3 className={styles.doneTitle}>
                    {sent.opened ? 'WhatsApp dibuka.' : 'Satu langkah lagi.'}
                </h3>
                <p className={styles.doneText}>
                    {sent.opened
                        ? 'Tekan kirim di WhatsApp. '
                        : 'Browser memblokir jendela baru. Klik Buka WhatsApp, lalu tekan kirim. '}
                    Setelah pesan terkirim, tim MGMX akan menghubungi{' '}
                    <span className={styles.doneWho}>{sent.nama}</span> di{' '}
                    <span className={styles.doneWho}>{sent.wa}</span> dalam 1 hari kerja untuk mengatur jadwal.
                </p>
                <div className={styles.doneActions}>
                    <a
                        className={clsx(styles.btn, styles.btnAmb)}
                        href={sent.url}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Buka WhatsApp
                    </a>
                    <button type="button" className={clsx(styles.btn, styles.btnGhost)} onClick={handleReset}>
                        Isi ulang form
                    </button>
                </div>
            </div>
        );
    }

    return (
        <form className={styles.form} onSubmit={handleSubmit} noValidate aria-label="Form minta demo">
            <div className={styles.row2}>
                <div className={styles.field}>
                    <label htmlFor={ids.nama} className={styles.fieldLabel}>Nama lengkap</label>
                    <input
                        ref={namaRef}
                        id={ids.nama}
                        name="nama"
                        type="text"
                        autoComplete="name"
                        placeholder="Budi Santoso"
                        maxLength={80}
                        required
                        className={clsx(styles.inp, errors.nama && styles.inpErr)}
                        value={nama}
                        onChange={handleNamaChange}
                        {...fieldA11y('nama')}
                    />
                    {renderError('nama')}
                </div>
                <div className={styles.field}>
                    <label htmlFor={ids.pt} className={styles.fieldLabel}>Nama perusahaan</label>
                    <input
                        ref={ptRef}
                        id={ids.pt}
                        name="perusahaan"
                        type="text"
                        autoComplete="organization"
                        placeholder="PT Maju Ekspedisi"
                        maxLength={100}
                        required
                        className={clsx(styles.inp, errors.pt && styles.inpErr)}
                        value={pt}
                        onChange={handlePtChange}
                        {...fieldA11y('pt')}
                    />
                    {renderError('pt')}
                </div>
            </div>
            <div className={styles.row2}>
                <div className={styles.field}>
                    <label htmlFor={ids.wa} className={styles.fieldLabel}>No. WhatsApp</label>
                    <span className={clsx(styles.pre, errors.wa && styles.preErr)}>
                        <span className={styles.preBox} aria-hidden="true">+62</span>
                        <input
                            ref={waRef}
                            id={ids.wa}
                            name="whatsapp"
                            type="tel"
                            inputMode="tel"
                            autoComplete="tel-national"
                            placeholder="812 3456 7890"
                            required
                            className={clsx(styles.inp, errors.wa && styles.inpErr)}
                            value={wa}
                            onChange={handleWaChange}
                            {...fieldA11y('wa')}
                        />
                    </span>
                    {renderError('wa')}
                </div>
                <div className={styles.field}>
                    <label htmlFor={ids.email} className={styles.fieldLabel}>
                        Email kantor <em className={styles.fieldOpt}>(opsional)</em>
                    </label>
                    <input
                        ref={emailRef}
                        id={ids.email}
                        name="email"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        placeholder="budi@majuekspedisi.co.id"
                        maxLength={120}
                        className={clsx(styles.inp, errors.email && styles.inpErr)}
                        value={email}
                        onChange={handleEmailChange}
                        {...fieldA11y('email')}
                    />
                    {renderError('email')}
                </div>
            </div>

            <div className={styles.chipGroup} role="group" aria-labelledby={ids.modul}>
                <span id={ids.modul} className={styles.fieldLabel}>
                    Modul yang paling dibutuhkan <em className={styles.fieldOpt}>(opsional, boleh lebih dari satu)</em>
                </span>
                <div className={styles.chips}>
                    {MODUL_OPTIONS.map((opt) => (
                        <button
                            key={opt}
                            type="button"
                            className={styles.chip}
                            aria-pressed={modul.includes(opt)}
                            onClick={() => toggleModul(opt)}
                        >
                            {opt}
                        </button>
                    ))}
                </div>
            </div>

            <button type="submit" className={clsx(styles.btn, styles.btnAmb, styles.submit)} aria-describedby={ids.note}>
                Kirim Permintaan Demo
            </button>
            <p id={ids.note} className={styles.fine}>
                Pesan dikirim lewat WhatsApp Anda. Tim kami akan membalas dalam 1 hari kerja.
            </p>
        </form>
    );
}

// ─── Paket ────────────────────────────────────────────────────────────────────

const IS_SAAS = LANDING_MODE === 'saas';

/**
 * Mode 'wa'  : PAKET dari landingConfig, CTA → WhatsApp.
 * Mode 'saas': paket & harga dari GET /api/public/plans (master_plans, sama dengan yang ditagih),
 *              CTA → /signup; gagal ambil → fallback PAKET (harga final tetap ditampilkan di /signup).
 */
function PaketSection() {
    const [pakets, setPakets] = useState<readonly Paket[] | null>(IS_SAAS ? null : PAKET);

    useEffect(() => {
        if (!IS_SAAS) return;
        let alive = true;
        api.get<{ plans?: PublicPlan[] }>('/public/plans')
            .then((res) => {
                const rows = res.data?.plans ?? [];
                if (alive) setPakets(rows.length > 0 ? mergePublicPlans(rows) : PAKET);
            })
            .catch(() => {
                if (alive) setPakets(PAKET);
            });
        return () => {
            alive = false;
        };
    }, []);

    return (
        <section id="paket" className={styles.sec} aria-labelledby="lp-paket-h">
            <div className={styles.secH}>
                <h2 id="lp-paket-h" className={styles.secTitle}>Pilih paket sesuai skala bisnis Anda.</h2>
                <p className={styles.secText}>
                    Semua paket sudah termasuk update aplikasi, backup harian, dan akses lewat browser dari mana saja.
                    {IS_SAAS && (
                        <>
                            {' '}Belum yakin?{' '}
                            <Link className={styles.pkgTrialLink} href="/trial">Coba gratis {TRIAL_DAYS} hari</Link>.
                        </>
                    )}
                </p>
            </div>
            {pakets === null ? (
                <p className={styles.secText} role="status">Memuat paket…</p>
            ) : (
                <ul className={styles.pkgs}>
                    {pakets.map((p) => {
                        const selfSignup = IS_SAAS && p.hargaBulan !== null;
                        const ctaClass = clsx(styles.btn, p.unggulan ? styles.btnAmb : styles.btnGhost, styles.pkgCta);
                        const ctaLabel = p.hargaBulan === null ? 'Konsultasi' : `Pilih ${p.nama}`;
                        return (
                            <li key={p.kode} className={clsx(styles.pkg, p.unggulan && styles.pkgHot)}>
                                {p.unggulan && <span className={styles.pkgBadge}>Paling populer</span>}
                                <h3 className={styles.pkgName}>{p.nama}</h3>
                                <p className={styles.pkgTag}>{p.tagline}</p>
                                <div className={styles.pkgPrice}>
                                    {p.hargaBulan === null ? (
                                        <b className={styles.pkgAmount}>Hubungi kami</b>
                                    ) : (
                                        <>
                                            <b className={styles.pkgAmount}>{formatRupiah(p.hargaBulan)}</b>
                                            <span className={styles.pkgPer}>/ bulan</span>
                                        </>
                                    )}
                                </div>
                                <ul className={styles.pkgFeats}>
                                    {p.fitur.map((f) => (
                                        <li key={f} className={styles.pkgFeat}>{f}</li>
                                    ))}
                                </ul>
                                {p.keuntungan && p.keuntungan.length > 0 && (
                                    <div className={styles.pkgPerks}>
                                        <b className={styles.pkgPerksTitle}>Keuntungan yang Anda dapatkan</b>
                                        <ul className={styles.pkgPerkList}>
                                            {p.keuntungan.map((k) => (
                                                <li key={k}>{k}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {selfSignup ? (
                                    <Link className={ctaClass} href={buildSignupUrl(p.kode)}>{ctaLabel}</Link>
                                ) : (
                                    <a
                                        className={ctaClass}
                                        href={buildWaUrl(buildPaketMessage(p.nama))}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {ctaLabel}
                                    </a>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
            <p className={styles.pkgNote}>
                Harga per bulan. Minimal berlangganan {MIN_BERLANGGANAN_BULAN} bulan
                {IS_SAAS ? ', ditagih per 6 atau 12 bulan.' : '.'}
            </p>
            <p className={clsx(styles.pkgNote, styles.pkgNoteTight)}>
                *) Untuk penjelasan lebih detail hubungi{' '}
                <a className={styles.pkgTrialLink} href={WA_CHAT_URL} target="_blank" rel="noopener noreferrer">
                    Tim marketing kami
                </a>
            </p>

            <div className={styles.addons}>
                <h3 className={styles.addonsTitle}>Penambahan user &amp; add-on</h3>
                <ul className={styles.addonList}>
                    {ADDONS.map((a) => (
                        <li key={a.nama} className={styles.addon}>
                            <span className={styles.addonText}>
                                <b className={styles.addonName}>{a.nama}</b>
                                <span className={styles.addonDesc}>{a.ket}</span>
                            </span>
                            <span className={styles.addonPrice}>
                                {formatRupiah(a.hargaBulan)}<small>/ bulan</small>
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
}

// ─── Halaman ──────────────────────────────────────────────────────────────────

export default function LandingPage() {
    const router = useRouter();
    const hasToken = useSyncExternalStore(subscribeAuth, getHasToken, getServerHasToken);
    const gateActive = hasToken;

    // Fallback sisi klien. Muat penuh ditangani script inline di page.tsx sebelum paint;
    // jalur ini untuk navigasi klien (mis. router.push('/') dari AccessDenied).
    // Login di tab lain TIDAK terdeteksi (zustand persist tidak mendengarkan event
    // `storage`) — tertangani script inline saat halaman di-reload.
    useEffect(() => {
        if (gateActive) {
            router.replace('/welcome');
        }
    }, [gateActive, router]);

    // Sudah login → landing tidak dirender sama sekali (cegah flash). Semua hook di atas.
    if (gateActive) return null;

    return (
        <div className={clsx(landingFontVars, styles.root)}>
            <header className={styles.wrap}>
                <nav className={styles.nav} aria-label="Navigasi utama">
                    <Logo />
                    <div className={styles.links}>
                        <a href="#paket">Paket</a>
                        <a href="#modul">Modul</a>
                        <a href="#alur">Alur</a>
                        <a href="#migrasi">Migrasi</a>
                        <a href="#demo">Minta Demo</a>
                    </div>
                    <Link href="/login" className={styles.navLogin}>Masuk</Link>
                    <a className={clsx(styles.btn, styles.btnGhost, styles.navCta)} href="#demo">Jadwalkan Demo</a>
                </nav>
            </header>

            <main className={styles.wrap}>
                <div className={styles.hero}>
                    <div className={styles.heroGrid}>
                        <div className={styles.heroCopy}>
                            <span className={styles.eyebrow}>ERP Berbasis WEB untuk Usaha Retail hingga Manufaktur</span>
                            <h1 className={styles.heroTitle}>
                                Optimalkan proses jual beli Anda dengan{' '}
                                <span className={styles.heroEm}>software terbaik!</span>
                            </h1>
                            <p className={styles.lead}>
                                Dapatkan solusi mengelola inventaris, memantau penjualan, dan meningkatkan efisiensi
                                bisnis Anda. Segera miliki software kami dan tingkatkan produktivitas bisnis Anda sekarang!
                            </p>
                            <div className={styles.ctaRow}>
                                {/* SaaS: trial jadi CTA utama; MGMX biasa: demo dipandu jadi CTA utama */}
                                {IS_SAAS && (
                                    <Link className={clsx(styles.btn, styles.btnAmb)} href="/trial">
                                        Coba Gratis {TRIAL_DAYS} Hari
                                    </Link>
                                )}
                                <a className={clsx(styles.btn, IS_SAAS ? styles.btnGhost : styles.btnAmb)} href="#demo">
                                    Jadwalkan Demo
                                </a>
                            </div>
                        </div>
                        <AppPreview />
                    </div>
                </div>

                <PaketSection />

                <section id="modul" className={clsx(styles.sec, styles.secFlush)} aria-labelledby="lp-modul-h">
                    <div className={styles.secH}>
                        <h2 id="lp-modul-h" className={styles.secTitle}>Semua bagian kantor bekerja di data yang sama.</h2>
                        <p className={styles.secText}>
                            Transaksi dari gudang dan penjualan langsung menjadi jurnal. Bagian keuangan tidak perlu input ulang.
                        </p>
                    </div>
                    <ul className={styles.mods}>
                        {MODULES.map((m) => (
                            <li key={m.kode} className={styles.mod}>
                                <span className={styles.modCode}>{m.kode}</span>
                                <h3 className={styles.modTitle}>{m.nama}</h3>
                                <p className={styles.modText}>{m.ket}</p>
                            </li>
                        ))}
                    </ul>
                </section>

                <section id="alur" className={clsx(styles.sec, styles.secFlush)} aria-labelledby="lp-alur-h">
                    <div className={styles.secH}>
                        <h2 id="lp-alur-h" className={styles.secTitle}>Dari PO sampai neraca, jurnalnya jalan sendiri.</h2>
                    </div>
                    <ol className={styles.flow}>
                        {FLOW_STEPS.map((s) => (
                            <li key={s.no} className={styles.step}>
                                <span className={styles.stepNo} aria-hidden="true">{s.no}</span>
                                <b className={styles.stepName}>{s.nama}</b>
                                <span className={styles.stepDesc}>{s.ket}</span>
                            </li>
                        ))}
                    </ol>
                </section>

                <section id="migrasi" className={clsx(styles.sec, styles.secFlush)} aria-labelledby="lp-migrasi-h">
                    <div className={styles.mig}>
                        <div>
                            <span className={styles.eyebrow}>Untuk pengguna MGMX desktop</span>
                            <h2 id="lp-migrasi-h" className={styles.migTitle}>Pindah ke web tanpa belajar dari nol.</h2>
                            <p className={styles.secText}>
                                Nama menu, kode perkiraan, dan alur transaksinya sama dengan versi desktop yang sudah dipakai tim Anda.
                            </p>
                        </div>
                        <div>
                            <ul className={styles.migList}>
                                {MIG_POINTS.map((p) => (
                                    <li key={p.judul} className={styles.migItem}>
                                        <span><b className={styles.migStrong}>{p.judul}</b> {p.ket}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className={styles.migNote}>
                                *) Untuk versi program desktop sebelum versi 3 dan yang custom, perlu ada penyesuaian dan
                                migrasi data. Besaran biaya yang timbul dapat bervariasi. Harap kontak{' '}
                                <a className={styles.pkgTrialLink} href={WA_CHAT_URL} target="_blank" rel="noopener noreferrer">
                                    tim kami
                                </a>{' '}
                                untuk lebih detail.
                            </p>
                        </div>
                    </div>
                </section>

                <section id="demo" className={clsx(styles.sec, styles.secFlush)} aria-labelledby="lp-demo-h">
                    <div className={styles.demo}>
                        <div className={styles.demoCol}>
                            <div className={clsx(styles.secH, styles.secHTight)}>
                                <h2 id="lp-demo-h" className={styles.secTitle}>Minta demo dipandu tim kami.</h2>
                                <p className={styles.secText}>
                                    Sesi 45 menit lewat Zoom, atau kunjungan ke kantor Anda di area Surabaya.
                                </p>
                            </div>
                            <ol className={styles.gets}>
                                {DEMO_GETS.map((g, i) => (
                                    <li key={g.judul} className={styles.get}>
                                        <span className={styles.getNo} aria-hidden="true">{i + 1}</span>
                                        <span>
                                            <b className={styles.getTitle}>{g.judul}</b>
                                            <span className={styles.getText}>{g.ket}</span>
                                        </span>
                                    </li>
                                ))}
                            </ol>
                            {TESTIMONI && (
                                <figure className={styles.quote}>
                                    <blockquote className={styles.quoteText}>“{TESTIMONI.kutipan}”</blockquote>
                                    <figcaption className={styles.quoteSrc}>{TESTIMONI.sumber}</figcaption>
                                </figure>
                            )}
                        </div>
                        <div className={styles.demoCol}>
                            <DemoRequest />
                        </div>
                    </div>
                </section>
            </main>

            <footer className={styles.footer}>
                <div className={clsx(styles.wrap, styles.foot)}>
                    <div>
                        <Logo className={styles.footLogo} />
                        ERP Berbasis WEB untuk Usaha Retail hingga Manufaktur
                    </div>
                    <address className={styles.footAddr}>
                        <b className={styles.footTitle}>Alamat</b>
                        {CONTACT.alamat1}<br />{CONTACT.alamat2}
                    </address>
                    <div>
                        <b className={styles.footTitle}>Kontak</b>
                        Telp <a className={styles.footLink} href={CONTACT.telpHref}>{CONTACT.telp}</a><br />
                        WhatsApp{' '}
                        <a className={styles.footLink} href={WA_CHAT_URL} target="_blank" rel="noopener noreferrer">
                            {CONTACT.whatsapp}
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
}
